import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year, month) {
    // Monday-based
    const d = new Date(year, month, 1).getDay();
    return d === 0 ? 6 : d - 1;
}

export default function DateTimePicker({ value, onChange, placeholder = 'Sana tanlang...' }) {
    const { t, i18n } = useTranslation();
    const isRu = i18n.language === 'ru';
    
    const months = isRu 
        ? ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'] 
        : ['Yanvar','Fevral','Mart','Aprel','May','Iyun','Iyul','Avgust','Sentabr','Oktabr','Noyabr','Dekabr'];
        
    const daysName = isRu
        ? ['Пн','Вт','Ср','Чт','Пт','Сб','Вс']
        : ['Du','Se','Ch','Pa','Ju','Sh','Ya'];

    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Parse value (ISO or datetime-local string)
    const parsed = value ? new Date(value) : null;
    const isValid = parsed && !isNaN(parsed);

    const today = new Date();
    const [viewYear, setViewYear] = useState(isValid ? parsed.getFullYear() : today.getFullYear());
    const [viewMonth, setViewMonth] = useState(isValid ? parsed.getMonth() : today.getMonth());
    const [selDay, setSelDay] = useState(isValid ? parsed.getDate() : null);
    const [selHour, setSelHour] = useState(isValid ? String(parsed.getHours()).padStart(2,'0') : '23');
    const [selMin, setSelMin] = useState(isValid ? String(parsed.getMinutes()).padStart(2,'0') : '59');

    // Close on outside click
    useEffect(() => {
        function handleClick(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    function getHour() {
        let h = parseInt(selHour, 10);
        return isNaN(h) ? 0 : h;
    }

    function getMin() {
        let m = parseInt(selMin, 10);
        return isNaN(m) ? 0 : m;
    }

    function emit(day, hour, min) {
        const d = new Date(viewYear, viewMonth, day, hour, min, 0);
        // format as datetime-local string
        const pad = n => String(n).padStart(2, '0');
        const str = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(hour)}:${pad(min)}`;
        onChange(str);
    }

    function selectDay(day) {
        setSelDay(day);
        emit(day, getHour(), getMin());
    }

    function clear(e) {
        e.stopPropagation();
        setSelDay(null);
        onChange('');
    }

    function prevMonth() {
        if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
        else setViewMonth(m => m - 1);
    }
    function nextMonth() {
        if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
        else setViewMonth(m => m + 1);
    }

    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

    const displayStr = isValid
        ? `${String(parsed.getDate()).padStart(2,'0')}.${String(parsed.getMonth()+1).padStart(2,'0')}.${parsed.getFullYear()}  ${String(parsed.getHours()).padStart(2,'0')}:${String(parsed.getMinutes()).padStart(2,'0')}`
        : '';

    return (
        <div className="relative" ref={ref}>
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 text-left transition-colors hover:bg-surface-2"
            >
                <Calendar className="w-4 h-4 text-muted flex-shrink-0" />
                <span className={`flex-1 ${displayStr ? 'text-primary' : 'text-muted'}`}>
                    {displayStr || placeholder}
                </span>
                {displayStr && (
                    <span onClick={clear} className="p-0.5 rounded hover:bg-red-500/10 text-muted hover:text-red-400 transition-colors">
                        <X className="w-3.5 h-3.5" />
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {open && (
                <div className="absolute z-[9999] mt-2 left-0 bg-surface border border-border rounded-2xl shadow-2xl p-4 w-[300px]"
                    style={{ backdropFilter: 'blur(12px)' }}>

                    {/* Month header */}
                    <div className="flex items-center justify-between mb-3">
                        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-surface-2 text-muted hover:text-primary transition-colors">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="font-bold text-sm">
                            {months[viewMonth]} {viewYear}
                        </span>
                        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-surface-2 text-muted hover:text-primary transition-colors">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Day headers */}
                    <div className="grid grid-cols-7 mb-1">
                        {daysName.map(d => (
                            <div key={d} className="text-center text-[10px] font-bold text-muted py-1">{d}</div>
                        ))}
                    </div>

                    {/* Days grid */}
                    <div className="grid grid-cols-7 gap-0.5">
                        {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} />)}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
                            const isSel = day === selDay && isValid && parsed.getMonth() === viewMonth && parsed.getFullYear() === viewYear;
                            const isPast = new Date(viewYear, viewMonth, day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                            return (
                                <button
                                    key={day}
                                    type="button"
                                    disabled={isPast}
                                    onClick={() => selectDay(day)}
                                    className={`aspect-square text-xs rounded-lg font-medium transition-colors
                                        ${isSel ? 'bg-brand text-inverse' :
                                          isToday ? 'border border-brand text-brand' :
                                          isPast ? 'text-muted/30 cursor-not-allowed' :
                                          'hover:bg-surface-2 text-primary'}`}
                                >
                                    {day}
                                </button>
                            );
                        })}
                    </div>

                    {/* Time picker */}
                    <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted flex-shrink-0" />
                            <span className="text-xs text-muted font-semibold">{isRu ? 'Время:' : 'Vaqt:'}</span>
                            <div className="flex items-center gap-1 ml-auto">
                                {/* Hours */}
                                <input
                                    type="text"
                                    maxLength={2}
                                    value={selHour}
                                    onChange={e => {
                                        let val = e.target.value.replace(/\D/g, '');
                                        if (val !== '' && parseInt(val, 10) > 23) val = '23';
                                        setSelHour(val);
                                    }}
                                    onBlur={() => {
                                        let h = parseInt(selHour, 10);
                                        if (isNaN(h)) h = 0;
                                        setSelHour(String(h).padStart(2, '0'));
                                        if (selDay) emit(selDay, h, parseInt(selMin || '0', 10));
                                    }}
                                    className="w-12 px-2 py-1 text-center rounded-lg border border-border bg-surface-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand/30"
                                    placeholder="00"
                                />
                                <span className="font-bold text-muted">:</span>
                                {/* Minutes */}
                                <input
                                    type="text"
                                    maxLength={2}
                                    value={selMin}
                                    onChange={e => {
                                        let val = e.target.value.replace(/\D/g, '');
                                        if (val !== '' && parseInt(val, 10) > 59) val = '59';
                                        setSelMin(val);
                                    }}
                                    onBlur={() => {
                                        let m = parseInt(selMin, 10);
                                        if (isNaN(m)) m = 0;
                                        setSelMin(String(m).padStart(2, '0'));
                                        if (selDay) emit(selDay, parseInt(selHour || '0', 10), m);
                                    }}
                                    className="w-12 px-2 py-1 text-center rounded-lg border border-border bg-surface-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand/30"
                                    placeholder="00"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Confirm button */}
                    <button
                        type="button"
                        onClick={() => { if(selDay) { emit(selDay, getHour(), getMin()); setOpen(false); } }}
                        disabled={!selDay}
                        className="mt-3 w-full py-2 rounded-xl bg-brand text-inverse text-sm font-semibold disabled:opacity-40 hover:opacity-90 transition-opacity"
                    >
                        {isRu ? 'Подтвердить' : 'Tasdiqlash'}
                    </button>
                </div>
            )}
        </div>
    );
}
